import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import {
  Box,
  Button,
  Container,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";

import {
  ComplianceModel,
  IComplianceExtractionEntry,
  IComplianceReportRun,
  IComplianceTripLog,
  ITruckProfile,
} from "../../models/ComplianceModel";

type TripDrafts = Record<
  string,
  {
    generalDestination: string;
    vehicleOwner: string;
    truckCapacityGallons: string;
    gallonsDelivered: string;
  }
>;

const formatDate = (value?: string) =>
  value ? moment(value).format("YYYY-MM-DD") : "";

const rowsPerPageOptions = [5, 10, 25];
const formFieldStyle: React.CSSProperties = {
  flex: "1 1 180px",
  minWidth: 0,
};
const responsiveActionStyle: React.CSSProperties = {
  flex: "0 1 auto",
  minWidth: 120,
};
const tableWrapperStyle: React.CSSProperties = {
  // Compliance tables are intentionally wide because the county-facing fields need
  // to stay visible; on small screens we scroll the table instead of crushing columns.
  overflowX: "auto",
  width: "100%",
};

const paginateRows = <T,>(rows: T[], page: number, rowsPerPage: number) =>
  rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

const materialUiV4PaginationHandlers = (
  onChangePage: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    nextPage: number
  ) => void,
  onChangeRowsPerPage: (event: React.ChangeEvent<HTMLInputElement>) => void
) =>
  ({
    // Heroku builds against the pinned Material UI v4 types, which still expect these callback names.
    onChangePage,
    onChangeRowsPerPage,
  } as any);

const ComplianceHelpLegend = () => (
  <Tooltip
    interactive
    placement="bottom-start"
    title={
      <Box maxWidth={460} padding="0.5rem">
        <Typography variant="subtitle2" gutterBottom>
          How to read compliance reports
        </Typography>
        <Typography variant="body2" paragraph>
          Groundwater extraction rows can be manual admin overrides or imported
          from QuickBooks invoices and sales receipts by transaction date. Manual
          entries stay in control when both exist for the same day.
        </Typography>
        <Typography variant="body2" paragraph>
          Trip logs only count completed, billable deliveries with billing
          proof. Invoice and card/ACH sales receipt paths also need a positive
          delivery note; check payments can qualify after admin review once a
          succeeded QuickBooks sales receipt exists.
        </Typography>
        <Typography variant="body2" paragraph>
          A warning report still generates CSV/PDF files, but something needs
          review: missing extraction entries, trip fields that need admin fixes,
          or a QuickBooks line that looked water-related but could not be parsed
          into gallons.
        </Typography>
        <Typography variant="body2">
          The Date Range controls what you inspect on this page. Generate Report
          uses the selected report type and reference date to create the final
          daily, monthly, or annual export.
        </Typography>
      </Box>
    }
  >
    <IconButton
      aria-label="Compliance report help"
      size="small"
      style={{ marginLeft: "0.5rem" }}
    >
      <HelpOutlineIcon fontSize="small" />
    </IconButton>
  </Tooltip>
);

export const AdminComplianceView: React.FC = () => {
  const today = useMemo(() => moment().format("YYYY-MM-DD"), []);
  const defaultStart = useMemo(
    () => moment().subtract(30, "days").format("YYYY-MM-DD"),
    []
  );

  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(today);
  const [entryDate, setEntryDate] = useState(today);
  const [extractedGallons, setExtractedGallons] = useState("");
  const [extractionNotes, setExtractionNotes] = useState("");
  const [newTruckNumber, setNewTruckNumber] = useState("");
  const [newVehicleOwner, setNewVehicleOwner] = useState("");
  const [newTruckCapacity, setNewTruckCapacity] = useState("");
  const [reportType, setReportType] = useState<"daily" | "monthly" | "annual">(
    "monthly"
  );
  const [reportReferenceDate, setReportReferenceDate] = useState(today);
  const [statusMessage, setStatusMessage] = useState("");

  const [extractionEntries, setExtractionEntries] = useState<IComplianceExtractionEntry[]>([]);
  const [truckProfiles, setTruckProfiles] = useState<ITruckProfile[]>([]);
  const [tripLogs, setTripLogs] = useState<IComplianceTripLog[]>([]);
  const [reportRuns, setReportRuns] = useState<IComplianceReportRun[]>([]);
  const [tripDrafts, setTripDrafts] = useState<TripDrafts>({});
  const [extractionPage, setExtractionPage] = useState(0);
  const [extractionRowsPerPage, setExtractionRowsPerPage] = useState(10);
  const [tripPage, setTripPage] = useState(0);
  const [tripRowsPerPage, setTripRowsPerPage] = useState(10);
  const [reportPage, setReportPage] = useState(0);
  const [reportRowsPerPage, setReportRowsPerPage] = useState(10);

  // Load every compliance section together so the admin page stays consistent after any mutation.
  const loadData = async () => {
    const [entryRows, truckRows, tripRows, reportRows] = await Promise.all([
      ComplianceModel.getExtractionEntries(startDate, endDate),
      ComplianceModel.getTruckProfiles(),
      ComplianceModel.getTripLogs(startDate, endDate),
      ComplianceModel.getReportRuns(),
    ]);

    setExtractionEntries(entryRows);
    setTruckProfiles(truckRows);
    setTripLogs(tripRows);
    setReportRuns(reportRows);
    setExtractionPage(0);
    setTripPage(0);
    setReportPage(0);
    setTripDrafts(
      tripRows.reduce<TripDrafts>((acc, row) => {
        acc[row._id] = {
          generalDestination: row.generalDestination || "",
          vehicleOwner: row.vehicleOwner || "",
          truckCapacityGallons: row.truckCapacityGallons
            ? String(row.truckCapacityGallons)
            : "",
          gallonsDelivered: row.gallonsDelivered ? String(row.gallonsDelivered) : "",
        };
        return acc;
      }, {})
    );
  };

  const paginatedExtractionEntries = useMemo(
    () => paginateRows(extractionEntries, extractionPage, extractionRowsPerPage),
    [extractionEntries, extractionPage, extractionRowsPerPage]
  );
  const paginatedTripLogs = useMemo(
    () => paginateRows(tripLogs, tripPage, tripRowsPerPage),
    [tripLogs, tripPage, tripRowsPerPage]
  );
  const paginatedReportRuns = useMemo(
    () => paginateRows(reportRuns, reportPage, reportRowsPerPage),
    [reportRuns, reportPage, reportRowsPerPage]
  );

  useEffect(() => {
    loadData().catch((error) => {
      console.error(error);
      setStatusMessage("Failed to load compliance data.");
    });
  }, []);

  const handleRefresh = async () => {
    try {
      await loadData();
      setStatusMessage("Compliance data refreshed.");
    } catch (error) {
      console.error(error);
      setStatusMessage("Failed to refresh compliance data.");
    }
  };

  const handleSaveExtraction = async () => {
    try {
      await ComplianceModel.saveExtractionEntry({
        entryDate,
        extractedGallons: Number(extractedGallons),
        notes: extractionNotes,
      });
      setExtractedGallons("");
      setExtractionNotes("");
      await loadData();
      setStatusMessage("Extraction entry saved.");
    } catch (error) {
      console.error(error);
      setStatusMessage("Failed to save extraction entry.");
    }
  };

  // Truck profiles act as master data: many orders can share one truck profile keyed by truck number.
  const handleCreateTruckProfile = async () => {
    try {
      await ComplianceModel.createTruckProfile({
        truckNumber: newTruckNumber,
        vehicleOwner: newVehicleOwner,
        truckCapacityGallons: Number(newTruckCapacity),
      });
      setNewTruckNumber("");
      setNewVehicleOwner("");
      setNewTruckCapacity("");
      await loadData();
      setStatusMessage("Truck profile saved.");
    } catch (error) {
      console.error(error);
      setStatusMessage("Failed to save truck profile.");
    }
  };

  const handleDeleteTruckProfile = async (truckProfileId: string) => {
    try {
      await ComplianceModel.deleteTruckProfile(truckProfileId);
      await loadData();
      setStatusMessage("Truck profile deleted.");
    } catch (error) {
      console.error(error);
      setStatusMessage("Failed to delete truck profile.");
    }
  };

  const handleSyncTripLogs = async () => {
    try {
      const result = await ComplianceModel.syncTripLogs(startDate, endDate);
      await loadData();
      setStatusMessage(`Trip logs synced: ${result.syncedCount}`);
    } catch (error) {
      console.error(error);
      setStatusMessage("Failed to sync trip logs.");
    }
  };

  const handleTripDraftChange = (
    tripLogId: string,
    field: keyof TripDrafts[string],
    value: string
  ) => {
    setTripDrafts((prev) => ({
      ...prev,
      [tripLogId]: {
        ...prev[tripLogId],
        [field]: value,
      },
    }));
  };

  const handleSaveTripLog = async (tripLogId: string) => {
    const draft = tripDrafts[tripLogId];
    // Only the fields that admins can correct live here; order/date/source fields stay system-owned.
    try {
      await ComplianceModel.updateTripLog(tripLogId, {
        generalDestination: draft.generalDestination,
        vehicleOwner: draft.vehicleOwner,
        truckCapacityGallons: draft.truckCapacityGallons
          ? Number(draft.truckCapacityGallons)
          : undefined,
        gallonsDelivered: draft.gallonsDelivered
          ? Number(draft.gallonsDelivered)
          : undefined,
      });
      await loadData();
      setStatusMessage("Trip log updated.");
    } catch (error) {
      console.error(error);
      setStatusMessage("Failed to update trip log.");
    }
  };

  const handleGenerateReport = async () => {
    // Generation is intentionally separate from sync so admins can review/fix data before exporting.
    try {
      await ComplianceModel.generateReport({
        reportType,
        referenceDate: reportReferenceDate,
      });
      await loadData();
      setStatusMessage("Compliance report generated.");
    } catch (error) {
      console.error(error);
      setStatusMessage("Failed to generate compliance report.");
    }
  };

  return (
    <Container maxWidth="lg" style={{ padding: "1rem" }}>
      {/* This page keeps the full Phase 7 workflow in one place for internal staff. */}
      <Box display="flex" alignItems="center" flexWrap="wrap">
        <Typography variant="h4" gutterBottom>
          Compliance Reporting
        </Typography>
        <ComplianceHelpLegend />
      </Box>
      <Typography variant="body1" style={{ marginBottom: "1rem" }}>
        Admin tools for groundwater extraction logs, trip reviews, and county-ready exports.
      </Typography>
      {statusMessage ? (
        <Typography variant="body2" color="primary" style={{ marginBottom: "1rem" }}>
          {statusMessage}
        </Typography>
      ) : null}

      <Paper style={{ padding: "1rem", marginBottom: "1rem" }}>
        <Typography variant="h6" gutterBottom>
          Date Range
        </Typography>
        {/* Date range is a manual inspection/sync window; cron uses backend calendar logic instead. */}
        <Box display="flex" gridGap="1rem" alignItems="center" flexWrap="wrap">
          <TextField
            label="Start Date"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            style={formFieldStyle}
          />
          <TextField
            label="End Date"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            style={formFieldStyle}
          />
          <Button
            variant="outlined"
            color="primary"
            onClick={handleRefresh}
            style={responsiveActionStyle}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleSyncTripLogs}
            style={responsiveActionStyle}
          >
            Sync Trip Logs
          </Button>
        </Box>
      </Paper>

      <Paper style={{ padding: "1rem", marginBottom: "1rem" }}>
        <Typography variant="h6" gutterBottom>
          Daily Groundwater Extraction
        </Typography>
        <Box display="flex" gridGap="1rem" alignItems="center" flexWrap="wrap">
          <TextField
            label="Entry Date"
            type="date"
            value={entryDate}
            onChange={(e) => setEntryDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            style={formFieldStyle}
          />
          <TextField
            label="Extracted Gallons"
            value={extractedGallons}
            onChange={(e) => setExtractedGallons(e.target.value)}
            style={formFieldStyle}
          />
          <TextField
            label="Notes"
            value={extractionNotes}
            onChange={(e) => setExtractionNotes(e.target.value)}
            style={{ flex: "2 1 220px", minWidth: 0 }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveExtraction}
            style={responsiveActionStyle}
          >
            Save Entry
          </Button>
        </Box>
        <Box style={tableWrapperStyle}>
          <Table size="small" style={{ marginTop: "1rem", minWidth: 560 }}>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Extracted Gallons</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Notes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedExtractionEntries.map((entry) => (
                <TableRow key={entry._id}>
                  <TableCell>{entry.entryDayKey}</TableCell>
                  <TableCell>{entry.extractedGallons}</TableCell>
                  <TableCell>
                    {entry.source === "imported" ? "QuickBooks import" : "Manual"}
                  </TableCell>
                  <TableCell>{entry.notes || "N/A"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
        <TablePagination
          component="div"
          count={extractionEntries.length}
          page={extractionPage}
          rowsPerPage={extractionRowsPerPage}
          rowsPerPageOptions={rowsPerPageOptions}
          {...materialUiV4PaginationHandlers(
            (_event, nextPage) => setExtractionPage(nextPage),
            (event) => {
              setExtractionRowsPerPage(parseInt(event.target.value, 10));
              setExtractionPage(0);
            }
          )}
        />
      </Paper>

      <Paper style={{ padding: "1rem", marginBottom: "1rem" }}>
        <Typography variant="h6" gutterBottom>
          Truck Profiles
        </Typography>
        <Box display="flex" gridGap="1rem" alignItems="center" flexWrap="wrap">
          <TextField
            label="Truck Number"
            value={newTruckNumber}
            onChange={(e) => setNewTruckNumber(e.target.value)}
            style={formFieldStyle}
          />
          <TextField
            label="Vehicle Owner"
            value={newVehicleOwner}
            onChange={(e) => setNewVehicleOwner(e.target.value)}
            style={formFieldStyle}
          />
          <TextField
            label="Truck Capacity Gallons"
            value={newTruckCapacity}
            onChange={(e) => setNewTruckCapacity(e.target.value)}
            style={formFieldStyle}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleCreateTruckProfile}
            style={responsiveActionStyle}
          >
            Add Truck
          </Button>
        </Box>
        <Box style={tableWrapperStyle}>
          <Table size="small" style={{ marginTop: "1rem", minWidth: 560 }}>
            <TableHead>
              <TableRow>
                <TableCell>Truck Number</TableCell>
                <TableCell>Vehicle Owner</TableCell>
                <TableCell>Capacity</TableCell>
                <TableCell>Status</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {truckProfiles.map((truck) => (
                <TableRow key={truck._id}>
                  <TableCell>{truck.truckNumber}</TableCell>
                  <TableCell>{truck.vehicleOwner}</TableCell>
                  <TableCell>{truck.truckCapacityGallons}</TableCell>
                  <TableCell>{truck.active ? "Active" : "Inactive"}</TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      variant="outlined"
                      color="secondary"
                      onClick={() => handleDeleteTruckProfile(truck._id)}
                    >
                      Delete
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Paper>

      <Paper style={{ padding: "1rem", marginBottom: "1rem" }}>
        <Typography variant="h6" gutterBottom>
          Trip Log Review
        </Typography>
        {/* Rows here represent normalized compliance trips, not raw orders, so admins can fix gaps safely. */}
        <Box style={tableWrapperStyle}>
          <Table size="small" style={{ minWidth: 1040 }}>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Destination</TableCell>
                <TableCell>Truck</TableCell>
                <TableCell>Vehicle Owner</TableCell>
                <TableCell>Capacity</TableCell>
                <TableCell>Gallons Delivered</TableCell>
                <TableCell>Review Status</TableCell>
                <TableCell>Notes</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedTripLogs.map((tripLog) => {
                const draft = tripDrafts[tripLog._id] || {
                  generalDestination: "",
                  vehicleOwner: "",
                  truckCapacityGallons: "",
                  gallonsDelivered: "",
                };

                return (
                  <TableRow key={tripLog._id}>
                    <TableCell>{formatDate(tripLog.tripDate)}</TableCell>
                    <TableCell>
                      <TextField
                        value={draft.generalDestination}
                        onChange={(e) =>
                          handleTripDraftChange(
                            tripLog._id,
                            "generalDestination",
                            e.target.value
                          )
                        }
                      />
                    </TableCell>
                    <TableCell>{tripLog.truckNumber || "N/A"}</TableCell>
                    <TableCell>
                      <TextField
                        value={draft.vehicleOwner}
                        onChange={(e) =>
                          handleTripDraftChange(
                            tripLog._id,
                            "vehicleOwner",
                            e.target.value
                          )
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        value={draft.truckCapacityGallons}
                        onChange={(e) =>
                          handleTripDraftChange(
                            tripLog._id,
                            "truckCapacityGallons",
                            e.target.value
                          )
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        value={draft.gallonsDelivered}
                        onChange={(e) =>
                          handleTripDraftChange(
                            tripLog._id,
                            "gallonsDelivered",
                            e.target.value
                          )
                        }
                      />
                    </TableCell>
                    <TableCell>{tripLog.reviewStatus}</TableCell>
                    <TableCell>{tripLog.reviewNotes.join(" ") || "Ready"}</TableCell>
                    <TableCell>
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={() => handleSaveTripLog(tripLog._id)}
                      >
                        Save
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
        <TablePagination
          component="div"
          count={tripLogs.length}
          page={tripPage}
          rowsPerPage={tripRowsPerPage}
          rowsPerPageOptions={rowsPerPageOptions}
          {...materialUiV4PaginationHandlers(
            (_event, nextPage) => setTripPage(nextPage),
            (event) => {
              setTripRowsPerPage(parseInt(event.target.value, 10));
              setTripPage(0);
            }
          )}
        />
      </Paper>

      <Paper style={{ padding: "1rem", marginBottom: "1rem" }}>
        <Typography variant="h6" gutterBottom>
          Generate Report
        </Typography>
        {/* Reference date anchors the reporting period: day/month/year depends on the selected type. */}
        <Box display="flex" gridGap="1rem" alignItems="center" flexWrap="wrap">
          <TextField
            select
            SelectProps={{ native: true }}
            label="Report Type"
            value={reportType}
            onChange={(e) =>
              setReportType(e.target.value as "daily" | "monthly" | "annual")
            }
            InputLabelProps={{ shrink: true }}
            style={formFieldStyle}
          >
            <option value="daily">Daily</option>
            <option value="monthly">Monthly</option>
            <option value="annual">Annual</option>
          </TextField>
          <TextField
            label="Reference Date"
            type="date"
            value={reportReferenceDate}
            onChange={(e) => setReportReferenceDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            style={formFieldStyle}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleGenerateReport}
            style={responsiveActionStyle}
          >
            Generate Report
          </Button>
        </Box>
      </Paper>

      <Paper style={{ padding: "1rem" }}>
        <Typography variant="h6" gutterBottom>
          Generated Reports
        </Typography>
        {/* Report history doubles as the artifact download center for both cron and admin-generated runs. */}
        <Box style={tableWrapperStyle}>
          <Table size="small" style={{ minWidth: 760 }}>
            <TableHead>
              <TableRow>
                <TableCell>Type</TableCell>
                <TableCell>Period</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Generated By</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Warnings</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedReportRuns.map((reportRun) => (
                <TableRow key={reportRun._id}>
                  <TableCell>{reportRun.reportType}</TableCell>
                  <TableCell>{reportRun.periodKey}</TableCell>
                  <TableCell>{reportRun.status}</TableCell>
                  <TableCell>{reportRun.generatedByType}</TableCell>
                  <TableCell>{moment(reportRun.createdAt).format("YYYY-MM-DD HH:mm")}</TableCell>
                  <TableCell>{reportRun.warningMessages.join(" ") || "None"}</TableCell>
                  <TableCell>
                    <Box display="flex" gridGap="0.5rem">
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => ComplianceModel.downloadReport(reportRun._id, "csv")}
                      >
                        CSV
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => ComplianceModel.downloadReport(reportRun._id, "pdf")}
                      >
                        PDF
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
        <TablePagination
          component="div"
          count={reportRuns.length}
          page={reportPage}
          rowsPerPage={reportRowsPerPage}
          rowsPerPageOptions={rowsPerPageOptions}
          {...materialUiV4PaginationHandlers(
            (_event, nextPage) => setReportPage(nextPage),
            (event) => {
              setReportRowsPerPage(parseInt(event.target.value, 10));
              setReportPage(0);
            }
          )}
        />
      </Paper>
    </Container>
  );
};